import { useAxiosModule } from '@/modules/_shared/axios'
import * as types from './types'
import { Member } from '@/modules/members/types'
import { AgreementSearchFilter } from '@/views/_shared/search-list/types'
import { isUUID } from '@/modules/_shared/helpers'

const {
    getFromApi,
    postToApi,
    putToApi,
    patchToApi,
    deleteInApi
} = useAxiosModule()

/* Get Agreements List */

export type GetAgreementsListResponse = types.GetAgreementsList

export const getAgreements = async (search = {} as AgreementSearchFilter, orderBy = 'createdAt desc', limit = 0, offset = 0) => {
    const queryParams = new URLSearchParams()
    if (isUUID(search.id)) {
        queryParams.set('id', search.id)
    } else if (parseInt(search.id) > 0) {
        queryParams.set('shortId', search.id)
    }

    queryParams.set('status', search.status)
    queryParams.set('name', search.name)
    queryParams.set('type', search.type)
    queryParams.set('organizationId', search.organization)
    queryParams.set('dateFrom', search.dateFrom)
    queryParams.set('dateTo', search.dateTo)
    queryParams.set('sort', orderBy)
    queryParams.set('limit', String(limit))
    queryParams.set('offset', String(offset))
    search.packageIds.forEach(packageId => {
        if (isUUID(packageId)) {
            queryParams.append('packageIds[]', packageId)
        }
    })
    const getFilteredAgreementsUrl = 'agreements?' + queryParams.toString()
    return getFromApi<GetAgreementsListResponse>(getFilteredAgreementsUrl)
}

/* Get Agreements simplified list */
export type GetAgreementsSimplifiedListResponse = types.AgreementsSimplifiedListModel[]
export const getAgreementsSimplifiedList = async () =>
    getFromApi<GetAgreementsSimplifiedListResponse>('agreements/simplified')

/* Get Agreement */

export type GetAgreementResponse = types.Agreement
export const getAgreement = async (agreementId: string) =>
    getFromApi<GetAgreementResponse>(`agreements/${agreementId}`)

/* Get Agreement Billing Info */

export type GetAgreementBillingInfoResponse = types.GetAgreementBillingInfo
export const getAgreementBillingInfo = async (agreementId: string) =>
    getFromApi<GetAgreementBillingInfoResponse>(`agreements/${agreementId}/billing-info`)

/* Get Agreement Scheduled Prices */
export type GetAgreementScheduledPricesResponse = types.AgreementScheduledPrice[]
export const getAgreementScheduledPrices = async (agreementId: string) =>
    getFromApi<GetAgreementScheduledPricesResponse>(`agreements/${agreementId}/scheduled-prices`)

/* Create Agreement Scheduled Price */
export type CreateAgreementScheduledPriceRequest = types.AgreementScheduledPriceCreatePayload
export const createAgreementScheduledPrice = async (agreementId: string, req: CreateAgreementScheduledPriceRequest) =>
    postToApi<null>(`agreements/${agreementId}/create-scheduled-price`, req)

/* Update Agreement Scheduled Price */
export type UpdateAgreementScheduledPriceRequest = types.AgreementScheduledPriceUpdatePayload
export const updateAgreementScheduledPrice = async (agreementId: string, req: UpdateAgreementScheduledPriceRequest) =>
    putToApi<null>(`agreements/${agreementId}/update-scheduled-price`, req)

/* Delete Agreement Scheduled Price */
export type DeleteAgreementScheduledPriceRequest = types.AgreementScheduledPriceDeletePayload
export const deleteAgreementScheduledPrice = async (agreementId: string, req: DeleteAgreementScheduledPriceRequest) =>
    deleteInApi<null>(`agreements/${agreementId}/delete-scheduled-price`, req)

/* Agreement Contact Info */
export type CreateAgreementContactInfoRequest = types.AgreementContactInfoCreatePayload
export const createAgreementContactInfo = async (agreementId: string, req: CreateAgreementContactInfoRequest) =>
    postToApi<null>(`agreements/${agreementId}/contact-persons`, req)

export type CreateDraftAgreementContactInfoRequest = types.AgreementContactInfoCreatePayload
export const createDraftAgreementContactInfo = async (agreementId: string, req: CreateDraftAgreementContactInfoRequest) =>
    postToApi<null>(`draft-agreements/${agreementId}/contact-persons`, req)

export type UpdateAgreementContactInfoRequest = types.AgreementContactInfoUpdatePayload
export const updateAgreementContactInfo = async (agreementId: string, req: UpdateAgreementContactInfoRequest) =>
    patchToApi<null>(`agreements/${agreementId}/contact-persons`, req)

export type UpdateDraftAgreementContactInfoRequest = types.AgreementContactInfoUpdatePayload
export const updateDraftAgreementContactInfo = async (agreementId: string, req: UpdateDraftAgreementContactInfoRequest) =>
    patchToApi<null>(`draft-agreements/${agreementId}/contact-persons`, req)

export type DeleteAgreementContactInfoRequest = types.AgreementContactInfoDeletePayload
export const deleteAgreementContactInfo = async (agreementId: string, req: DeleteAgreementContactInfoRequest) =>
    deleteInApi<null>(`agreements/${agreementId}/contact-persons`, req)

export type DeleteDraftAgreementContactInfoRequest = types.AgreementContactInfoDeletePayload
export const deleteDraftAgreementContactInfo = async (agreementId: string, req: DeleteDraftAgreementContactInfoRequest) =>
    deleteInApi<null>(`draft-agreements/${agreementId}/contact-persons`, req)

/* Add Draft Agreement */

export type AddDraftAgreementRequest = types.AgreementPayload
export const addDraftAgreement = async (req: AddDraftAgreementRequest) =>
    postToApi<{ agreementId: string }>('draft-agreements/create', req)

/* Change Agreement Billing Info Mail */
export type ChangeBillingInfoMailRequest = types.AgreementBillingInfoMailPayload
export const changeAgreementBillingInfoMail = async (agreementId: string, payload: ChangeBillingInfoMailRequest) =>
    patchToApi<null>(`agreements/${agreementId}/change-billing-info/mail`, payload)

/* Change Agreement Billing Info Email */
export type ChangeBillingInfoEmailRequest = types.AgreementBillingInfoEmailPayload
export const changeAgreementBillingInfoEmail = async (agreementId: string, payload: ChangeBillingInfoEmailRequest) =>
    patchToApi<null>(`agreements/${agreementId}/change-billing-info/email`, payload)

/* Change Agreement Billing Info Electronic */
export type ChangeBillingInfoElectronicRequest = types.AgreementBillingInfoElectronicPayload
export const changeAgreementBillingInfoElectronic = async (agreementId: string, payload: ChangeBillingInfoElectronicRequest) =>
    patchToApi<null>(`agreements/${agreementId}/change-billing-info/electronic`, payload)

/* Get Agreement Members */

export type GetAgreementMembersResponse = types.AgreementMember[]
export const getAgreementMembers = async (id: string) =>
    getFromApi<GetAgreementMembersResponse>(`agreements/${id}/members`)

/* Get Agreement Available Members */

export type GetAgreementAvailableMembersResponse = Member[]
export const getAgreementAvailableMembers = async (id: string) =>
    getFromApi<GetAgreementAvailableMembersResponse>(`agreements/${id}/members-available`)

/* Add member to Agreement */

export const addMemberToAgreement = async (agreementId: string, memberId: string) =>
    postToApi<null>(`agreements/${agreementId}/add-member`, {
        agreementId: agreementId,
        memberId: memberId
    })

export type SwapMemberRequest = types.SwapMember
export const swapAgreementMember = async (agreementId: string, payload: SwapMemberRequest) =>
    patchToApi<null>(`agreements/${agreementId}/swap-member`, payload)

export type CancelMemberRequest = types.CancelMember
export const cancelAgreementMember = async (agreementId: string, payload: CancelMemberRequest) =>
    deleteInApi<null>(`agreements/${agreementId}/cancel-access`, payload)

export type PurgeMemberRequest = types.PurgeMember
export const purgeAgreementMember = async (agreementId: string, payload: PurgeMemberRequest) =>
    deleteInApi<null>(`agreements/${agreementId}/purge-access`, payload)

export type ResetAgreementMemberAccessEndDateRequest = types.PurgeMember
export const resetAgreementMemberAccess = async (agreementId: string, payload: ResetAgreementMemberAccessEndDateRequest) =>
    postToApi<null>(`agreements/${agreementId}/reset-access`, payload)

/* Member Draft Agreements */

export const addMemberToDraftAgreement = async (agreementId: string, memberId: string) =>
    postToApi<null>(`draft-agreements/${agreementId}/add-member`, {
        agreementId: agreementId,
        memberId: memberId
    })

export const cancelDraftAgreementMember = async (agreementId: string, payload: CancelMemberRequest) =>
    deleteInApi<null>(`draft-agreements/${agreementId}/cancel-access`, payload)

/*  Update Agreement */

export const updateAgreementName = async (id: string, name: string) =>
    patchToApi<null>(`agreements/${id}/change-name`, {
        agreementId: id,
        name
    })

export const updateAgreementPrice = async (id: string, price: number) =>
    patchToApi<null>(`agreements/${id}/change-price`, {
        agreementId: id,
        price
    })

export const updateAgreementInvoicingAllowedFrom = async (id: string, invoicingAllowedFrom: string) =>
    patchToApi<null>(`agreements/${id}/change-invoicing-from-date`, {
        agreementId: id,
        invoicingAllowedFrom
    })

export const updateAgreementEndDate = async (id: string, endDate: string) =>
    patchToApi<null>(`agreements/${id}/change-end-date`, {
        agreementId: id,
        endDate
    })

export const updateAgreementMaxMembers = async (id: string, maxMembers: number) =>
    patchToApi<null>(`agreements/${id}/change-member-limit`, {
        agreementId: id,
        memberLimit: maxMembers
    })

export const updateAgreementAdditionalInfo = async (id: string, additionalInfo: string | null) =>
    patchToApi<null>(`agreements/${id}/change-additional-info`, { additionalInfo })

export const cancelAgreement = async (id: string, immediatelyCancelled: boolean) =>
    deleteInApi<null>(`agreements/${id}/cancel`, {
        agreementId: id,
        immediatelyCancelled: immediatelyCancelled
    })

export const updateAgreementPackages = async (id: string, packages: string[]) =>
    patchToApi<null>(`agreements/${id}/change-packages`, {
        agreementId: id,
        packages: packages
    })

export const disableAgreementForm = async (id: string) =>
    patchToApi<null>(`agreements/${id}/disable-form`, { agreementId: id })

export const enableAgreementForm = async (id: string) =>
    patchToApi<null>(`agreements/${id}/enable-form`, { agreementId: id })

export const denyAgreementMarketingCommunication = async (id: string) =>
    patchToApi<null>(`agreements/${id}/deny-marketing-communication`, { agreementId: id })

export const allowAgreementMarketingCommunication = async (id: string) =>
    patchToApi<null>(`agreements/${id}/allow-marketing-communication`, { agreementId: id })

export const enableDigitalClassroomsExclusion = async (id: string) =>
    patchToApi<null>(`agreements/${id}/enable-digital-classrooms-exclusion`, { agreementId: id })

export const disableDigitalClassroomsExclusion = async (id: string) =>
    patchToApi<null>(`agreements/${id}/disable-digital-classrooms-exclusion`, { agreementId: id })

export const enableAllowLSPExport = async (id: string) =>
    patchToApi<null>(`agreements/${id}/enable-allow-lsp-export`)

export const disableAllowLSPExport = async (id: string) =>
    patchToApi<null>(`agreements/${id}/disable-allow-lsp-export`)

export const enableEditInSelfService = async (id: string) =>
    patchToApi<null>(`agreements/${id}/enable-edit-in-self-service`)

export const disableEditInSelfService = async (id: string) =>
    patchToApi<null>(`agreements/${id}/disable-edit-in-self-service`)

// Draft Agreement methods

export const updateDraftAgreementName = async (id: string, name: string) =>
    patchToApi<null>(`draft-agreements/${id}/change-name`, {
        agreementId: id,
        name
    })

export const updateDraftAgreementPrice = async (id: string, price: number) =>
    patchToApi<null>(`draft-agreements/${id}/change-price`, {
        agreementId: id,
        price
    })

export const updateDraftAgreementInvoicingAllowedFrom = async (id: string, invoicingAllowedFrom: string) =>
    patchToApi<null>(`draft-agreements/${id}/change-invoicing-from-date`, {
        agreementId: id,
        invoicingAllowedFrom
    })

export const updateDraftAgreementEndDate = async (id: string, endDate: string) =>
    patchToApi<null>(`draft-agreements/${id}/change-end-date`, {
        agreementId: id,
        endDate
    })

export const updateDraftAgreementMaxMembers = async (id: string, maxMembers: number) =>
    patchToApi<null>(`draft-agreements/${id}/change-member-limit`, {
        agreementId: id,
        memberLimit: maxMembers
    })

export const updateDraftAgreementPackages = async (id: string, packages: string[]) =>
    patchToApi<null>(`draft-agreements/${id}/change-packages`, {
        agreementId: id,
        packages: packages
    })

export const updateDraftAgreementAdditionalInfo = async (id: string, additionalInfo: string | null) =>
    patchToApi<null>(`draft-agreements/${id}/change-additional-info`, { additionalInfo })

export const disableDraftAgreementForm = async (id: string) =>
    patchToApi<null>(`draft-agreements/${id}/disable-form`, { agreementId: id })

export const enableDraftAgreementForm = async (id: string) =>
    patchToApi<null>(`draft-agreements/${id}/enable-form`, { agreementId: id })

export const denyDraftAgreementMarketingCommunication = async (id: string) =>
    patchToApi<null>(`draft-agreements/${id}/deny-marketing-communication`, { agreementId: id })

export const allowDraftAgreementMarketingCommunication = async (id: string) =>
    patchToApi<null>(`draft-agreements/${id}/allow-marketing-communication`, { agreementId: id })

export const enableDraftDigitalClassroomsExclusion = async (id: string) =>
    patchToApi<null>(`draft-agreements/${id}/enable-digital-classrooms-exclusion`, { agreementId: id })

export const disableDraftDigitalClassroomsExclusion = async (id: string) =>
    patchToApi<null>(`draft-agreements/${id}/disable-digital-classrooms-exclusion`, { agreementId: id })

export const enableDraftAllowLSPExport = async (id: string) =>
    patchToApi<null>(`draft-agreements/${id}/enable-allow-lsp-export`)

export const disableDraftAllowLSPExport = async (id: string) =>
    patchToApi<null>(`draft-agreements/${id}/disable-allow-lsp-export`)

export const enableDraftEditInSelfService = async (id: string) =>
    patchToApi<null>(`draft-agreements/${id}/enable-edit-in-self-service`)

export const disableDraftEditInSelfService = async (id: string) =>
    patchToApi<null>(`draft-agreements/${id}/disable-edit-in-self-service`)

export const markAsReadyForPublication = async (id: string, readyForPublication: boolean) =>
    patchToApi<null>(`draft-agreements/${id}/ready-for-publication`, { agreementId: id, readyForPublication: readyForPublication })

// Change Billing Info Organization Name

export type UpdateBillingInfoOrganizationNameRequest = types.ChangeBillingInfoOrganizationName
export const updateBillingInfoOrganizationName = async (agreementId: string, billingInfoId: string, req: UpdateBillingInfoOrganizationNameRequest) =>
    patchToApi<null>(`agreements/${agreementId}/billing-info/${billingInfoId}/change-organization-name`, req)

// Change Billing Info Business Id

export type UpdateBillingInfoBusinessIdRequest = types.ChangeBillingInfoBusinessId
export const updateBillingInfoBusinessId = async (agreementId: string, billingInfoId: string, req: UpdateBillingInfoBusinessIdRequest) =>
    patchToApi<null>(`agreements/${agreementId}/billing-info/${billingInfoId}/change-business-id`, req)

// Change Billing Info Payment Terms
export type UpdateBillingInfoPaymentTermsRequest = types.ChangeBillingInfoPaymentTerms
export const updateBillingInfoPaymentTerms = async (agreementId: string, billingInfoId: string, req: UpdateBillingInfoPaymentTermsRequest) =>
    patchToApi<null>(`agreements/${agreementId}/billing-info/${billingInfoId}/change-payment-terms`, req)

// Change Billing Info Additional Info

export type UpdateBillingInfoAdditionalInfoRequest = types.ChangeBillingInfoAdditionalInfo
export const updateBillingInfoAdditionalInfo = async (agreementId: string, billingInfoId: string, req: UpdateBillingInfoAdditionalInfoRequest) =>
    patchToApi<null>(`agreements/${agreementId}/billing-info/${billingInfoId}/change-additional-info`, req)

// Change Billing Info Contact Data

export type UpdateBillingInfoContactDataRequest = types.ChangeBillingInfoContactData
export const updateBillingInfoContactData = async (agreementId: string, billingInfoId: string, req: UpdateBillingInfoContactDataRequest) =>
    patchToApi<null>(`agreements/${agreementId}/billing-info/${billingInfoId}/change-contact`, req)

// Change Billing Info Address

export type UpdateBillingInfoAddressRequest = types.ChangeBillingInfoAddress
export const updateBillingInfoAddress = async (agreementId: string, billingInfoId: string, req: UpdateBillingInfoAddressRequest) =>
    patchToApi<null>(`agreements/${agreementId}/billing-info/${billingInfoId}/change-address`, req)

// Change Billing Info Invoice Description

export type UpdateBillingInfoInvoiceDescriptionRequest = types.ChangeBillingInfoInvoiceDescription
export const updateBillingInfoInvoiceDescription = async (agreementId: string, billingInfoId: string, req: UpdateBillingInfoInvoiceDescriptionRequest) =>
    patchToApi<null>(`agreements/${agreementId}/billing-info/${billingInfoId}/change-invoice-description`, req)

// Change Billing Info Invoice Reference
export type UpdateBillingInfoInvoiceReferenceRequest = types.ChangeBillingInfoInvoiceReference
export const updateBillingInfoInvoiceReference = async (agreementId: string, billingInfoId: string, req: UpdateBillingInfoInvoiceReferenceRequest) =>
    patchToApi<null>(`agreements/${agreementId}/billing-info/${billingInfoId}/change-reference`, req)

// Change Billing Info VatId

export type UpdateBillingInfoVatIdRequest = types.ChangeBillingInfoVatId
export const updateBillingInfoVatId = async (agreementId: string, billingInfoId: string, req: UpdateBillingInfoVatIdRequest) =>
    patchToApi<null>(`agreements/${agreementId}/billing-info/${billingInfoId}/change-vat-id`, req)

// Change Billing Info Customer Number
export type UpdateBillingInfoCustomerNumberRequest = types.ChangeBillingInfoCustomerNumber
export const updateBillingInfoCustomerNumber = async (agreementId: string, billingInfoId: string, req: UpdateBillingInfoCustomerNumberRequest) =>
    patchToApi<null>(`agreements/${agreementId}/billing-info/${billingInfoId}/change-customer-number`, req)

/* Assign Electronic Delivery Policy */

export type AssignElectronicDeliveryPolicyRequest = types.AssignElectronicDeliveryPolicy
export const assignElectronicDeliveryPolicy = async (agreementId: string, billingInfoId: string, req: AssignElectronicDeliveryPolicyRequest) =>
    patchToApi<null>(`agreements/${agreementId}/billing-info/${billingInfoId}/assign-electronic-delivery-policy`, req)

/* Assign Email Delivery Policy */

export type AssignEmailDeliveryPolicyRequest = types.AssignEmailDeliveryPolicy
export const assignEmailDeliveryPolicy = async (agreementId: string, billingInfoId: string, req: AssignEmailDeliveryPolicyRequest) =>
    patchToApi<null>(`agreements/${agreementId}/billing-info/${billingInfoId}/assign-email-delivery-policy`, req)

/* Assign Mail Delivery Policy */

export type AssignMailDeliveryPolicyRequest = types.AssignMailDeliveryPolicy
export const assignMailDeliveryPolicy = async (agreementId: string, billingInfoId: string, req: AssignMailDeliveryPolicyRequest) =>
    patchToApi<null>(`agreements/${agreementId}/billing-info/${billingInfoId}/assign-mail-delivery-policy`, req)

/* Get Agreement Scheduled Prices */
export const getDraftAgreementScheduledPrices = async (agreementId: string) =>
    getFromApi<GetAgreementScheduledPricesResponse>(`draft-agreements/${agreementId}/scheduled-prices`)

/* Create Agreement Scheduled Price */
export const createDraftAgreementScheduledPrice = async (agreementId: string, req: CreateAgreementScheduledPriceRequest) =>
    postToApi<null>(`draft-agreements/${agreementId}/create-scheduled-price`, req)

/* Update Agreement Scheduled Price */
export const updateDraftAgreementScheduledPrice = async (agreementId: string, req: UpdateAgreementScheduledPriceRequest) =>
    putToApi<null>(`draft-agreements/${agreementId}/update-scheduled-price`, req)

/* Delete Agreement Scheduled Price */
export const deleteDraftAgreementScheduledPrice = async (agreementId: string, req: DeleteAgreementScheduledPriceRequest) =>
    deleteInApi<null>(`draft-agreements/${agreementId}/delete-scheduled-price`, req)

export const removeDraftAgreement = async (agreementId: string) =>
    deleteInApi<null>(`draft-agreements/${agreementId}`)

export type GetMembersInAgreementsResponse = types.GetMembersInAgreementsList

export const getMembersInAgreements = async (search = '') => {
    const queryParams = new URLSearchParams()

    queryParams.set('search', search)
    const getSearchMembersUrl = 'agreements/search-members?' + queryParams.toString()
    return getFromApi<GetMembersInAgreementsResponse>(getSearchMembersUrl)
}
